import { createServerFn } from '@tanstack/react-start';
import { getRequest } from '@tanstack/react-start/server';
import { z } from 'zod';

import {
	findOrAddInstrumentToDB,
	getInstrumentCompanyAndShareCountByTicker,
	getInstrumentsForCompany,
} from '~/database/supabase/instrument_company/queries.server.ts';
import { getCompanyReports } from '~/database/supabase/news/queries.server.ts';
import { getUserInstrumentSettings } from '~/database/supabase/settings/queries.server.ts';
import { fetchDisplayData } from '~/integrations/yahoo/queries.server.ts';
import { getHints } from '~/utils/client-hints.tsx';
import instrumentRanges from '~/views/instrument/utils/helpers.tsx';

import { requireStartAuthSession } from '../auth/require-start-auth-session.ts';
import { createServerFnObservabilityMiddleware } from '../utils/create-instrumented-server-fn.ts';

export interface UserInstrumentViewSettings {
	readonly hideAfter: boolean | null;
	readonly hideBefore: boolean | null;
	readonly hideBetween: boolean | null;
	readonly userComparisonKeys: string[];
	readonly userTimeRange: string;
}

const GetInstrumentLayoutPageDataSchema = z.object({
	ticker: z.string().optional(),
});

export const getInstrumentLayoutPageData = createServerFn({ method: 'GET' })
	.middleware([
		createServerFnObservabilityMiddleware({
			dependencies: [
				'auth.session',
				'supabase.instrument.find-or-add',
				'supabase.instrument.layout',
				'supabase.instrument.reports',
				'supabase.instrument.settings',
				'yahoo.display-data',
			],
			relatesTo: 'instrument.layout',
		}),
	])
	.inputValidator(GetInstrumentLayoutPageDataSchema)
	.handler(async ({ data }) => {
		const request = getRequest();
		const authSession = await requireStartAuthSession({ request });
		const userLocale = getHints(request).locale;
		const ticker = data.ticker;

		if (!ticker) {
			return {
				instrument: null,
				instrumentFetch: undefined,
				ranges: null,
				relatedInstruments: null,
				reports: null,
				userComparisonKeys: null,
				userInstrumentViewSettings: null,
				userLocale,
			};
		}

		const instrument = await findOrAddInstrumentToDB({ ticker });

		if (!instrument) {
			return {
				instrument: null,
				instrumentFetch: undefined,
				ranges: null,
				relatedInstruments: null,
				reports: null,
				userComparisonKeys: null,
				userInstrumentViewSettings: null,
				userLocale,
			};
		}

		const [instrumentFetch, userInstrumentSettings, companyReports, instrumentsForCompany, instrumentWithCompany] =
			await Promise.all([
				fetchDisplayData({ ticker }),
				getUserInstrumentSettings(authSession.userId),
				getCompanyReports(Number(instrument.company_id)),
				getInstrumentsForCompany({ companyId: Number(instrument.company_id) }),
				getInstrumentCompanyAndShareCountByTicker(ticker),
			]);

		const userTimeRange = '1d';
		const userComparisonKeys = ['^OMX', '^SPX'];
		const ranges = instrumentRanges();

		return {
			instrument: instrumentWithCompany,
			instrumentFetch,
			ranges,
			relatedInstruments: instrumentsForCompany,
			reports: companyReports,
			userComparisonKeys,
			userInstrumentViewSettings: {
				hideAfter: userInstrumentSettings?.hideAfter ?? null,
				hideBefore: userInstrumentSettings?.hideBefore ?? null,
				hideBetween: userInstrumentSettings?.hideBetween ?? null,
				userComparisonKeys,
				userTimeRange,
			} satisfies UserInstrumentViewSettings,
			userLocale,
		};
	});
